import utilService from "@/services/util.service";
import themeService, { TraderverseThemes } from "@/services/theme.service";
import { trackError } from "@/helpers/tracking.helpers";
import { TrackingEvents } from "./enum";
import { getPersistedLocale } from "@/locale/index";

const filters = {
  systemNumberConvention(
    input: any,
    isPrice: boolean = false,
    isAbs: boolean = false,
    containerWidth: number = 50,
    isRound: boolean = true,
    fromChart: boolean = false,
    alwaysShowTwoDecimal: boolean = false
  ) {
    try {
      if (input == null || isNaN(input)) {
        return "0";
      }
      if (typeof input == "string") {
        input = parseFloat(input);
      }
      var isNegative = false;
      if (input < 0) {
        isNegative = true;
      }
      input = Math.abs(input);
      var result: any = 0;
      var charToAdd = "";
      if (isRound) {
        if (input >= 1.0e12) {
          var trillion: any = (input / 1.0e12).toFixed(2);
          trillion = Math.round(trillion * 100) / 100;
          result = parseFloat(trillion).toString();
          charToAdd = "T";
        } else if (input >= 1.0e9) {
          var billion: any = (input / 1.0e9).toFixed(2);
          billion = Math.round(billion * 100) / 100;
          result = parseFloat(billion).toString();
          charToAdd = "B";
        } else if (input >= 1.0e6) {
          var million: any = (input / 1.0e6).toFixed(2);
          million = Math.round(million * 100) / 100;
          result = parseFloat(million).toString();
          charToAdd = "M";
        } else if (input >= 10000) {
          result = (input / 1000).toFixed(2);
          result = parseFloat(result).toString();
          charToAdd = "K";
        } else if (input >= 1000) {
          result = input.toFixed(0);
        } else if (input >= 100) {
          result = input.toFixed(2);
          result = parseFloat(result).toString();
        } else if (input >= 10) {
          result = input.toFixed(2);
          result = parseFloat(result).toString();
        } else if (input < 1 && input >= 0.0001) {
          result = input.toFixed(4);
          result = parseFloat(result).toString();
        } else if (input < 0.0001) {
          result = input.toString();
        } else {
          result = input.toFixed(2);
          result = parseFloat(result).toString();
        }
      } else {
        result = input.toFixed(2);
        result = parseFloat(result).toString();
      }

      if (isAbs) {
        result = Math.abs(result);
      }
      if (charToAdd) {
        result += charToAdd;
      }

      if (isPrice) {
        if (input >= 1000) {
          if (!isNaN(result) && typeof result === "string") {
            result = Number(result).toLocaleString();
          }
        }
        if (isNegative && !isAbs) {
          if (fromChart) {
            result = result;
          } else {
            result = `<span class="DollarSignChanging">$</span>${result}`;
          }
        } else {
          if (alwaysShowTwoDecimal) {
            if (!isNaN(result)) {
              result = parseFloat(result).toFixed(2);
            } else {
              result = result.toFixed(2);
            }
          } else if (fromChart) {
            result = result;
          } else {
            result = `<span class="DollarSignChanging">$</span>${result}`;
          }
        }
      } else {
        if (input < 1000 && input >= 1) {
          result = +input;
          result = Math.round(result * 100) / 100;
          result = +result;
        } else if (input < 1) {
          result = +input;
          result = result.toFixed(2);
          if (!isAbs && !isPrice && result == 0) {
            result = +input;
          }
        }
        if (isNegative && !isAbs) {
          result = `${result}`;
        }
      }
      return result;
    } catch (e) {
      trackError(e, {
        context: TrackingEvents.systemNumberConvention,
        input,
        isPrice,
        isAbs,
        containerWidth,
        isRound,
        fromChart,
        alwaysShowTwoDecimal,
      });
      return "0";
    }
  },

  fetchLowestValues(input: number, containerWidth: number) {
    try {
      let fontSize = 14;
      let [mantissaStr, exponentStr] = input.toExponential().split("e");
      let exponent = parseInt(exponentStr, 10);
      let mantissa = parseFloat(mantissaStr);
      if (exponent < 0) {
        let leadingZeros = Math.abs(exponent) - 2;
        const maxCharacters = Math.floor(containerWidth / fontSize);
        let decimalPlaces = Math.max(0, maxCharacters - 4);
        let significantDigits = (mantissa * Math.pow(10, leadingZeros))
          .toFixed(decimalPlaces)
          .replace(".", "")
          .replace(/0+$/, "")
          .substring(0, decimalPlaces);
        let result = `0.0{${leadingZeros}}${significantDigits}`;
        return result;
      } else {
        return input.toString();
      }
    } catch (e) {
      trackError(e, {
        context: TrackingEvents.fetchLowestValues,
        input,
        containerWidth,
      });
      return input.toString();
    }
  },

  calculateDecimalPlaces(number: any, containerWidth: any) {
    try {
      let fontSize = 14;
      const numberStr = number.toString();
      const integerPartLength = numberStr.split(".")[0].length;
      const availableWidth = containerWidth - integerPartLength * fontSize;
      const maxDecimalDigits = Math.max(
        0,
        Math.floor(availableWidth / fontSize)
      );
      return maxDecimalDigits;
    } catch (e) {
      trackError(e, {
        context: TrackingEvents.calculateDecimalPlaces,
        number,
        containerWidth,
      });
      return 2; // Default value
    }
  },

  systemNumberConventionOld(
    input: any,
    isPrice: boolean = false,
    isAbs: boolean = false,
    containerWidth: number = 50,
    isRound: boolean = true,
    fromChart: boolean = false
  ) {
    try {
      if (input == null || isNaN(Number(input))) {
        return "0";
      }

      input = Number(input);
      if (isAbs) {
        input = Math.abs(input);
      }
      var isNegative = false;
      if (input < 0) {
        isNegative = true;
      }
      input = Math.abs(input);
      var result: any = 0;
      var charToAdd = "";
      if (isRound) {
        if (input >= 1.0e12) {
          var trillion: any = (input / 1.0e12).toFixed(2);
          trillion = Math.round(trillion * 100) / 100;
          result = parseFloat(trillion).toString();
          charToAdd = "T";
        } else if (input >= 1.0e9) {
          var billion: any = (input / 1.0e9).toFixed(2);
          billion = Math.round(billion * 100) / 100;
          result = parseFloat(billion).toString();
          charToAdd = "B";
        } else if (input >= 1.0e6) {
          var million: any = (input / 1.0e6).toFixed(2);
          million = Math.round(million * 100) / 100;
          result = parseFloat(million).toString();
          charToAdd = "M";
        } else if (input >= 10000) {
          result = (input / 1000).toFixed(2);
          result = parseFloat(result).toString();
          charToAdd = "K";
        } else if (input >= 1000) {
          result = input.toFixed(0);
        } else if (input >= 100) {
          result = input.toFixed(2);
          result = parseFloat(result).toString();
        } else if (input >= 10) {
          result = input.toFixed(2);
          result = parseFloat(result).toString();
        } else if (input < 1 && input >= 0.0001) {
          result = input.toFixed(4);
          result = parseFloat(result).toString();
        } else if (input < 0.0001) {
          result = this.fetchLowestValues(input, containerWidth);
        } else {
          result = input.toFixed(2);
          result = parseFloat(result).toString();
        }
      } else {
        result = input.toFixed(2);
        result = parseFloat(result).toString();
      }

      if (isAbs) {
        result = Math.abs(result);
      }
      if (charToAdd) {
        result += charToAdd;
      }

      if (isPrice) {
        if (input >= 1000) {
          if (!isNaN(result) && typeof result === "string") {
            result = Number(result).toLocaleString();
          }
        }

        if (isNegative && !isAbs) {
          if (fromChart) {
            result = result;
          } else {
            result = `$${result}`;
          }
        } else {
          if (fromChart) {
            result = result;
          } else {
            result = `$${result}`;
          }
        }
      } else {
        if (input < 1000 && input >= 1) {
          result = +input;
          result = Math.round(result * 100) / 100;
          result = +result;
        } else if (input < 1) {
          result = +input;
          result = result.toFixed(2);
          if (!isAbs && !isPrice && result == 0) {
            result = +input;
          }
        }
        if (isNegative && !isAbs) {
          result = `${result}`;
        }
      }
      return result;
    } catch (e) {
      trackError(e, {
        context: TrackingEvents.systemNumberConvention,
        input,
        isPrice,
        isAbs,
        containerWidth,
        isRound,
        fromChart,
      });
      return "0";
    }
  },
  getComputedVariable(varName: string) {
    try {
      if (themeService.currentTheme == TraderverseThemes.light) {
        return utilService.chartTheme["light"][varName];
      } else {
        return utilService.chartTheme["default"][varName];
      }
    } catch (e) {
      trackError(e, {
        context: TrackingEvents.getComputedVariable,
        varName,
        currentTheme: themeService.currentTheme,
      });
      return "";
    }
  },

  insertSpaceBeforeCapitalLetter(s: string) {
    try {
      var val = s.replace(/([A-Z])/g, " $1").trim();
      return val;
    } catch (e) {
      trackError(e, {
        context: TrackingEvents.insertSpaceBeforeCapitalLetter,
        inputString: s,
      });
      return s;
    }
  },

  validStockLogoUrl(url: string) {
    try {
      return url && url != "n/a" && !url.includes("http:");
    } catch (e) {
      trackError(e, {
        context: TrackingEvents.validStockLogoUrl,
        url,
      });
      return false;
    }
  },

  convertToTitleCase(val: string) {
    try {
      if (val) {
        const trimmed = val.trim();
        if (trimmed.toLowerCase() == "nft") {
          return trimmed.toUpperCase();
        }
        if (trimmed.includes(" ")) {
          return trimmed
            .split(" ")
            .filter((w) => w) // remove empty strings from multiple spaces
            .map((w) => w[0].toUpperCase() + w.substring(1).toLowerCase())
            .join(" ");
        } else {
          return `${trimmed[0].toUpperCase()}${trimmed.substring(1)}`.trim();
        }
      } else {
        return "";
      }
    } catch (e) {
      trackError(e, {
        context: TrackingEvents.convertToTitleCase,
        value: val,
      });
      return val;
    }
  },

  convertToUpperCase(val: string) {
    try {
      if (val) {
        return val.toUpperCase();
      } else {
        return "";
      }
    } catch (e) {
      trackError(e, {
        context: TrackingEvents.convertToUpperCase,
        value: val,
      });
      return "";
    }
  },

  // formatDate(value: number, format: string = "MMM dd, yyyy") {
  //   if (value) {
  //     const months: string[] = [
  //       "Jan",
  //       "Feb",
  //       "Mar",
  //       "Apr",
  //       "May",
  //       "Jun",
  //       "July",
  //       "Aug",
  //       "Sept",
  //       "Oct",
  //       "Nov",
  //       "Dec",
  //     ];
  //     const date = new Date(value);
  //     const year = date.getFullYear();
  //     const monthIndex = date.getMonth();
  //     const day = date.getDate().toString().padStart(2, "0");

  //     // For month in numeric format with leading zero
  //     const monthNumeric = (monthIndex + 1).toString().padStart(2, "0");
  //     // For month in alphabetic format
  //     const monthAlpha = months[monthIndex];

  //     // Replace the format string with actual values
  //     let formattedDate = format
  //       .replace("yyyy", year.toString())
  //       .replace("mm", monthNumeric)
  //       .replace("dd", day)
  //       .replace("MMM", monthAlpha);

  //     return formattedDate;
  //   }
  //   return "";
  // },
  formatToMonthDay(dateInput, format = "MMM DD") {
    if (!dateInput) return "";

    let date;

    // Handle timestamp input
    if (typeof dateInput === "number") {
      date = new Date(dateInput);
    }
    // Handle ISO string or YYYY-MM-DD string
    else if (typeof dateInput === "string") {
      // If it's a number string like "1753401600000"
      if (/^\d+$/.test(dateInput)) {
        date = new Date(parseInt(dateInput, 10));
      } else {
        date = new Date(dateInput);
      }
    }

    if (isNaN(date?.getTime())) {
      console.warn("Invalid date input:", dateInput);
      return "";
    }

    const year = date.getFullYear().toString();
    const monthIndex = date.getMonth();
    const dayNum = date.getDate().toString().padStart(2, "0");

    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const monthAlpha = monthNames[monthIndex];
    const monthNum = (monthIndex + 1).toString().padStart(2, "0");

    const formattedDate = format
      .replace(/yyyy|YYYY/, year)
      .replace(/MMM/, monthAlpha)
      .replace(/MM/, monthNum)
      .replace(/dd|DD/, dayNum);

    return formattedDate;
  },

  convertToMMDDYY(dateString) {
    try {
      const [year, month, day] = dateString.split("-");
      const shortYear = year.slice(-2);
      return `${month}/${day}/${shortYear}`;
    } catch (e) {
      return dateString;
    }
  },

  formatDate(
    value: number | string | Date,
    format: string = "MMM dd, yyyy",
    locale: string = getPersistedLocale() || "en"
  ) {
    try {
      if (!value) return "";

      // Convert to Date object
      let date;
      if (typeof value === "number") {
        date = new Date(value > 9999999999 ? value : value * 1000);
      } else if (typeof value === "string") {
        if (/^\d+$/.test(value)) {
          const num = Number(value);
          date = new Date(num > 9999999999 ? num : num * 1000);
        } else {
          date = new Date(value);
        }
      } else {
        date = value;
      }

      if (isNaN(date.getTime())) return "";

      // Locale-aware date parts
      const options: Intl.DateTimeFormatOptions = {
        month: "short", // e.g., "Jan" or "أكتوبر"
        day: "2-digit",
        year: "numeric",
      };

      // Use Intl to get localized month/day/year
      const formatter = new Intl.DateTimeFormat(locale, options);
      const parts = formatter.formatToParts(date);

      const partMap = Object.fromEntries(parts.map((p) => [p.type, p.value]));

      // Fallbacks for custom formatting tokens
      const monthAlpha = partMap.month || "";
      const day = partMap.day || "";
      const year = partMap.year || "";
      const yearShort = year.slice(-2);

      const monthIndex = date.getMonth();
      const monthNumeric = (monthIndex + 1).toString();
      const monthPadded = monthNumeric.padStart(2, "0");

      // Replace your tokens in format string
      return format
        .replace("yyyy", year)
        .replace("yy", yearShort)
        .replace("mm", monthPadded)
        .replace("m", monthNumeric)
        .replace("dd", day)
        .replace("d", String(parseInt(day)))
        .replace("MMM", monthAlpha);
    } catch (e) {
      console.error("formatDate error:", e);
      return "";
    }
  },

  // formatDate(value: number | string | Date, format: string = "MMM dd, yyyy") {
  //   try {
  //     if (!value) return "";

  //     // Convert to Date object
  //     let date: Date;
  //     if (typeof value === 'number') {
  //       // Handle both seconds and milliseconds timestamps
  //       date = new Date(value > 9999999999 ? value : value * 1000);
  //     } else if (typeof value === 'string') {
  //       // Try to parse string (could be ISO string or timestamp string)
  //       if (/^\d+$/.test(value)) {
  //         const num = Number(value);
  //         date = new Date(num > 9999999999 ? num : num * 1000);
  //       } else {
  //         date = new Date(value);
  //       }
  //     } else {
  //       date = value;
  //     }

  //     // Check if date is valid
  //     if (isNaN(date.getTime())) return "";

  //     const months: string[] = [
  //       "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  //       "July", "Aug", "Sept", "Oct", "Nov", "Dec"
  //     ];

  //     const year = date.getFullYear();
  //     const yearShort = year.toString().slice(-2);
  //     const monthIndex = date.getMonth();
  //     const day = date.getDate();
  //     const dayPadded = day.toString().padStart(2, "0");
  //     const monthNumeric = (monthIndex + 1).toString();
  //     const monthPadded = monthNumeric.padStart(2, "0");
  //     const monthAlpha = months[monthIndex];

  //     return format
  //       .replace("yyyy", year.toString())
  //       .replace("yy", yearShort)
  //       .replace("mm", monthPadded)
  //       .replace("m", monthNumeric)
  //       .replace("dd", dayPadded)
  //       .replace("d", day.toString())
  //       .replace("MMM", monthAlpha);
  //   } catch (e) {
  //     trackError(e, {
  //       context: TrackingEvents.formatDate,
  //       value,
  //       format
  //     });
  //     return "";
  //   }
  // },
  // formatDate(value: number, format: string = "MMM dd, yyyy") {
  //   try {
  //     if (value) {
  //       const months: string[] = [
  //         "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  //         "July", "Aug", "Sept", "Oct", "Nov", "Dec"
  //       ];
  //       const date = new Date(value);
  //       const year = date.getFullYear();
  //       const yearShort = year.toString().slice(-2);
  //       const monthIndex = date.getMonth();
  //       const day = date.getDate();
  //       const dayPadded = day.toString().padStart(2, "0");
  //       const monthNumeric = (monthIndex + 1).toString();
  //       const monthPadded = monthNumeric.padStart(2, "0");
  //       const monthAlpha = months[monthIndex];

  //       let formattedDate = format
  //         .replace("yyyy", year.toString())
  //         .replace("yy", yearShort)
  //         .replace("mm", monthPadded)
  //         .replace("m", monthNumeric)
  //         .replace("dd", dayPadded)
  //         .replace("d", day.toString())
  //         .replace("MMM", monthAlpha);

  //       return formattedDate;
  //     }
  //     return "";
  //   } catch (e) {
  //     trackError(e, {
  //       context: TrackingEvents.formatDate,
  //       value,
  //       format
  //     });
  //     return "";
  //   }

  // },

  formatDateTwo(
    value: number | string | Date,
    format: string = "MMM dd",
    locale: string = getPersistedLocale() || "en"
  ): string {
    try {
      if (!value) return "";

      // Convert to Date object
      let date: Date;
      if (typeof value === "number") {
        date = new Date(value > 9999999999 ? value : value * 1000);
      } else if (typeof value === "string") {
        if (/^\d+$/.test(value)) {
          const num = Number(value);
          date = new Date(num > 9999999999 ? num : num * 1000);
        } else {
          date = new Date(value);
        }
      } else {
        date = value;
      }

      // Invalid date check
      if (isNaN(date.getTime())) return "";

      // Locale-aware formatter setup
      const options: Intl.DateTimeFormatOptions = {
        month: "short", // e.g., "Jan" or "أكتوبر"
        day: "2-digit",
        year: "numeric",
      };

      const formatter = new Intl.DateTimeFormat(locale, options);
      const parts = formatter.formatToParts(date);
      const partMap: Record<string, string> = Object.fromEntries(
        parts.map((p) => [p.type, p.value])
      );

      const monthAlpha = partMap.month || "";
      const day = partMap.day || "";
      const year = partMap.year || "";

      const monthIndex = date.getMonth();
      const monthNum = (monthIndex + 1).toString().padStart(2, "0");

      // Token replacement (case-insensitive)
      const formattedDate = format
        .replace(/yyyy|YYYY/, year)
        .replace(/MMM/, monthAlpha)
        .replace(/MM/, monthNum)
        .replace(/dd|DD/, day)
        .replace(/d|D/, String(parseInt(day)));

      return formattedDate;
    } catch (e) {
      trackError(e, {
        context: TrackingEvents.formatDateTwo,
        value,
        format,
      });
      return "";
    }
  },

  parseNumberIfInString(stat: string) {
    try {
      if (!stat) {
        return 0;
      }
      var pattern = /[a-zA-Z]/;
      if (!pattern.test(stat)) {
        stat = filters.systemNumberConvention(stat);
      }
      return stat;
    } catch (e) {
      trackError(e, {
        context: TrackingEvents.parseNumberIfInString,
        stat,
      });
      return 0;
    }
  },

  validateNumberInput(
    evt: any,
    limit: number = -1,
    allowDecimal: boolean = true,
    positiveOnly: boolean = false
  ) {
    try {
      const isDecimalPoint = allowDecimal && evt.key === ".";
      const input = typeof evt === "string" ? evt : evt.target.value + evt.key;

      if (
        // Allow backspace, delete, and decimal point if allowed
        evt.key === "Backspace" ||
        evt.key === "Delete" ||
        isDecimalPoint ||
        // Allow numbers
        (!isNaN(Number(evt.key)) && !evt.ctrlKey) ||
        // Allow Ctrl+A, Ctrl+C, Ctrl+V
        (evt.ctrlKey && ["a", "c", "v"].includes(evt.key.toLowerCase())) ||
        // Allow if length is within limit
        (limit !== -1 && input.length <= limit)
      ) {
        // If limit is set, check if the input value exceeds the limit
        if (limit !== -1) {
          const inputValue = parseFloat(input);
          if (isNaN(inputValue) || inputValue > limit) {
            evt.preventDefault();
          }
        }
        if ((positiveOnly && input.includes("-")) || input <= 0) {
          evt.preventDefault();
        }
      } else {
        evt.preventDefault();
      }
    } catch (e) {
      trackError(e, {
        context: TrackingEvents.validateNumberInput,
        event: evt,
        limit,
        allowDecimal,
        positiveOnly,
      });
      evt.preventDefault();
    }
  },

  validateStockSymbol(symbol: string): string {
    try {
      if (symbol?.includes("-")) {
        symbol = symbol.split("-")[0];
      }
      if (symbol?.length > 6) {
        symbol = symbol.slice(0, 6);
      }
      return symbol;
    } catch (e) {
      trackError(e, {
        context: TrackingEvents.validateStockSymbol,
        symbol,
      });
      return symbol;
    }
    // if (symbol.includes('.')) {
    //   symbol = symbol.split('.')[0];
    // }
  },

  randomGeneratedValue() {
    return Math.floor(Math.random() * (30000 - 800 + 1)) + 800;
  },

  getStatsValue(key: string, value: any) {
    try {
      var isPrice = true;

      if (
        key == "followersCount" ||
        key == "followingCount" ||
        key == "friendCount" ||
        key == "investmentsCount" ||
        key == "trades" ||
        key == "avgTradesPerMonth" ||
        key == "avgTradesPerWeek" ||
        key == "viewsCount" ||
        key == "outreach" ||
        key == "avgGain" ||
        key == "topGain" ||
        key == "tradesThisMonth" ||
        key == "tradesThisWeek" ||
        key == "todayTrades" ||
        key == "tradesThisYear" ||
        key == "traderTypeCount" ||
        key == "bestTradeCount" ||
        key == "favoritePlatformCount" ||
        key == "recommendationCount" ||
        key == "watchListCount" ||
        key == "listCount" ||
        key == "linksCount" ||
        key == "imagesCount" ||
        key == "favoriteInfluencerCount" ||
        key == "favoriteInvestmentCount" ||
        key == "playbookCount" ||
        key == "profitAndLossCount" ||
        key == "predictionCount" ||
        key == "socialLnksCount"
      ) {
        isPrice = false;
      }
      var newValue = value;
      newValue = filters.systemNumberConvention(
        newValue,
        isPrice,
        false,
        50,
        isPrice
      );

      if (!isNaN(newValue)) {
        newValue = newValue.toString();
        newValue = parseFloat(newValue);
      }
      if (key == "avgGain" || key == "topGain") {
        newValue += "%";
      }
      return newValue;
    } catch (e) {
      trackError(e, {
        context: TrackingEvents.getStatsValue,
        key,
        value,
      });
      return value;
    }
  },

  getProfitPercentage(purchasedPrice: number, soldPrice: number) {
    try {
      const profit = soldPrice - purchasedPrice;
      if (purchasedPrice === 0) {
        return "Cannot calculate profit percentage when purchase price is zero.";
      }
      const profitPercentage = (profit / purchasedPrice) * 100;
      return profitPercentage || NaN;
    } catch (e) {
      trackError(e, {
        context: TrackingEvents.getClassByProfit,
        purchasedPrice,
        soldPrice,
      });
      return "profitDisabled";
    }
  },

  getClassByProfit(purchasedPrice: number, soldPrice: number) {
    try {
      const profitPercentage = +this.getProfitPercentage(
        purchasedPrice,
        soldPrice
      );
      let profitClass = "ProfitDisabled";
      if (profitPercentage >= 20 && profitPercentage < 50) {
        profitClass = "bronze";
      } else if (profitPercentage >= 50 && profitPercentage < 100) {
        profitClass = "silver";
      } else if (profitPercentage >= 100) {
        profitClass = "gold";
      } else {
        profitClass = "profitDisabled";
      }

      return profitClass;
    } catch (e) {
      trackError(e, {
        context: TrackingEvents.getClassByProfit,
        purchasedPrice,
        soldPrice,
      });
      return "profitDisabled";
    }
  },
  getClassByGainPercentage(gainPercent: number) {
    try {
      let profitClass = "ProfitDisabled";
      if (gainPercent >= 20 && gainPercent < 50) {
        profitClass = "bronze";
      } else if (gainPercent >= 50 && gainPercent < 100) {
        profitClass = "silver";
      } else if (gainPercent >= 100) {
        profitClass = "gold";
      } else {
        profitClass = "profitDisabled";
      }

      return profitClass;
    } catch (e) {
      return "profitDisabled";
    }
  },
  calcFunc(postTradingDTO) {
    try {
      if (
        postTradingDTO &&
        postTradingDTO.orders &&
        postTradingDTO.orders.length > 0
      ) {
        const sortedOrders = postTradingDTO.orders.sort(
          (a, b) =>
            new Date(a.orderDate).getTime() - new Date(b.orderDate).getTime()
        );
        let position = sortedOrders[0].type === "sell" ? "short" : "long";
        let tradeType =
          Array.from(new Set(postTradingDTO.orders.map((x) => x.orderDate)))
            .length === 1
            ? "dayTrade"
            : "swing";
        let tradeStatus = "";
        let orderTypes = postTradingDTO.orders.map((x) => x.type);
        let sellShareQuantity = postTradingDTO.orders
          .filter((x) => x.type === "sell")
          .map((x) => x.shares)
          .reduce((a, b) => a + b, 0);
        let buyShareQuantity = postTradingDTO.orders
          .filter((x) => x.type === "buy")
          .map((x) => x.shares)
          .reduce((a, b) => a + b, 0);
        let shares =
          sellShareQuantity == 0 ? buyShareQuantity : sellShareQuantity;
        let buyOrders = postTradingDTO.orders.filter((x) => x["type"] == "buy");
        let sellOrders = postTradingDTO.orders.filter(
          (x) => x["type"] == "sell"
        );
        let pprice = 0;
        let totalBuyPrice = buyOrders
          .map((x) => x["price"])
          .reduce((a, b) => a + b, 0);
        let totalSellPrice = sellOrders
          .map((x) => x["price"])
          .reduce((a, b) => a + b, 0);
        if (orderTypes.includes("buy") && orderTypes.includes("sell")) {
          if (sellShareQuantity == buyShareQuantity) {
            tradeStatus = "close";
          } else {
            tradeStatus = "partiallyClosed";
          }
        } else if (orderTypes.includes("buy")) {
          tradeStatus = "open";
        } else if (orderTypes.includes("sell")) {
          tradeStatus = "open";
        }
        if (buyShareQuantity > 0) {
          pprice = totalBuyPrice / buyShareQuantity;
        } else if (buyShareQuantity === 0 && sellShareQuantity > 0) {
          pprice = totalSellPrice / sellShareQuantity;
        }
        let finalGain = 0;
        let finalGainPercent = 0;
        let totalPrice = 0;
        if (totalSellPrice > 0) {
          totalPrice = totalSellPrice - totalPrice;
        } else {
          totalPrice = totalBuyPrice;
        }
        if (totalSellPrice > 0) {
          let avgBuyingPrice =
            buyOrders.length > 0 ? totalBuyPrice / buyOrders.length : 0;
          let avgSellingPrice =
            sellOrders.length > 0 ? totalSellPrice / sellOrders.length : 0;
          let totalSoldShares = sellOrders
            .map((x) => x["shares"])
            .reduce((a, b) => a + b, 0);
          let costPrice = avgBuyingPrice * totalSoldShares;
          let sellingPriceTotal = avgSellingPrice * totalSoldShares;
          finalGain = sellingPriceTotal - costPrice;
          finalGainPercent =
            costPrice !== 0 ? (finalGain / costPrice) * 100 : 0;
        }
        let tradeObj = {
          symbol: postTradingDTO.symbol ?? "",
          stockId: postTradingDTO.stockId ?? "",
          companyName: postTradingDTO.companyName ?? "",
          stockType: postTradingDTO.stockType ?? "",
          position: position ?? "",
          tradeType: tradeType ?? "",
          tradeStatus: tradeStatus ?? "",
          shares: isNaN(shares) ? 0 : shares,
          pprice: isNaN(pprice) ? 0 : pprice,
          gain: isNaN(finalGain) ? 0 : finalGain,
          totalPrice: isNaN(totalPrice) ? 0 : totalPrice,
          gainPercent:
            isNaN(finalGainPercent) || !isFinite(finalGainPercent)
              ? 0
              : finalGainPercent,
          totalOrders: isNaN(postTradingDTO.orders?.length)
            ? 0
            : postTradingDTO.orders.length,
          orders: postTradingDTO.orders ?? [],
          _id: postTradingDTO._id ?? "",
          isPrivate: postTradingDTO.isPrivate ?? true,
        };
        //   console.log(tradeObj, 'tradeObj');
        return tradeObj;
      }
      return {};
    } catch (e) {
      trackError(e, {
        context: TrackingEvents.calcFunc,
        postTradingDTO,
      });
      return {};
    }
  },
  getFormattedTrades(list: any = []) {
    try {
      let profits = list
        .filter((item) => item.trades.length > 0)
        .map((item) =>
          this.calcFunc({
            _id: item._id,
            symbol: item.trades[0]?.symbol || "",
            stockId: item.stockId,
            companyName: item.companyName,
            stockType: item.stockType,
            orders: item.trades.map((trade) => ({
              type: trade.side,
              shares: trade.Qty,
              price: trade.limit_price,
              orderDate: trade.created_at,
            })),
            position: "long",
            tradeType: "Swing Trade",
            tradeStatus: "open",
            pprice: 0,
            shares: 0,
            totalPrice: 0,
            gainPercent: 0,
            isPrivate: item.isPrivate,
          })
        );
      return profits;
    } catch (e) {
      trackError(e, {
        context: TrackingEvents.getFormattedTrades,
        list,
      });
      return [];
    }
  },
  formatNumber(num: number) {
    try {
      if (isNaN(num) || typeof num !== "number") {
        return num;
      }
      if (num < 1000) {
        return num; // Return the number directly if less than 1000
      }

      // Define thresholds for suffixes
      const sizes = [
        { value: 1e12, suffix: "T" },
        { value: 1e9, suffix: "B" },
        { value: 1e6, suffix: "M" },
        { value: 1e3, suffix: "K" },
      ];

      // Iterate through sizes to find appropriate suffix
      for (let i = 0; i < sizes.length; i++) {
        if (num >= sizes[i].value) {
          return (num / sizes[i].value).toFixed(2) + sizes[i].suffix;
        }
      }
    } catch (e) {
      trackError(e, {
        context: TrackingEvents.formatNumber,
        num,
      });
      return num;
    }
  },

  formatNumberWithCommas(num: number) {
    // Check if the provided value is not a number
    if (isNaN(num) || typeof num !== "number") {
      return num;
    }

    // Use toLocaleString to format the number with commas
    return num.toLocaleString();
  },

  processValue(value: number): number {
    if (value > 100) {
      return 100;
    } else if (value % 1 !== 0) {
      // Check if the value has a decimal part
      return Math.floor(value); // Return only the integer part
    } else {
      return value;
    }
  },

  truncateText(text: string, maxLength: number = 45): string {
    if (text.length > maxLength) {
      return text.substring(0, maxLength) + "...";
    }
    return text;
  },

  systemAccuracyPercentage(
    input: any,
    containerWidth: number = 50,
    isRound: boolean = true,
    alwaysShowTwoDecimal: boolean = false
  ) {
    try {
      if (input == null || isNaN(input)) {
        return "0";
      }
      if (typeof input == "string") {
        input = parseFloat(input);
      }

      // Clamp the value between 0 and 100 for accuracy
      // If negative, set to 0 (or 1 if you prefer minimum 1%)
      if (input < 0) {
        input = 0; // You can change this to 1 if you want minimum 1%
      } else if (input > 100) {
        input = 100; // Cap at 100%
      }

      var result: any = 0;
      var charToAdd = "";

      if (isRound) {
        if (input >= 100) {
          result = input.toFixed(0);
        } else if (input >= 10) {
          result = input.toFixed(2);
          result = parseFloat(result).toString();
        } else if (input >= 1) {
          result = input.toFixed(2);
          result = parseFloat(result).toString();
        } else if (input < 1 && input >= 0.0001) {
          result = input.toFixed(4);
          result = parseFloat(result).toString();
        } else if (input < 0.0001) {
          result = this.fetchLowestValues(input, containerWidth);
        } else {
          result = input.toFixed(2);
          result = parseFloat(result).toString();
        }
      } else {
        result = input.toFixed(2);
        result = parseFloat(result).toString();
      }

      if (alwaysShowTwoDecimal) {
        if (!isNaN(result)) {
          result = parseFloat(result).toFixed(2);
        } else {
          result = result.toFixed(2);
        }
      } else {
        if (input < 100 && input >= 1) {
          result = +input;
          result = Math.round(result * 100) / 100;
          result = +result;
        } else if (input < 1) {
          result = +input;
          result = result.toFixed(2);
        }
      }

      return result;
    } catch (e) {
      trackError(e, {
        context: TrackingEvents.systemAccuracyPercentage,
        input,
        containerWidth,
        isRound,
        alwaysShowTwoDecimal,
      });
      return "0";
    }
  },
};

export default filters;
